import {
  TRANSACTION_STATUS,
  TRANSFER_STATUS,
  createTransferDetail,
  createTransferPage,
} from '@vezeeta/vpay-pack';

const valueOrFallback = (value, fallback) => value ?? fallback;

export const TRANSFERS_PAGE_SIZE = 10;
export const TRANSFERS_DESKTOP_MIN_WIDTH = 768;

export const createTransfersRequestError = () => {
  const error = new Error('Unable to load transfers');
  error.retryable = true;
  return error;
};

export const formatTransferDetailDate = value => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getProcessedAtLabel = (processedAt, formatDetailDate) => {
  if (!processedAt) return '';
  return formatDetailDate ? formatDetailDate(processedAt) : processedAt;
};

export const mapTransferDto = (dto, formatDate, formatDetailDate = formatDate) => ({
  id: dto?.payoutTransferKey,
  referenceNumber: dto?.referenceNumber,
  referenceNumberString: dto?.referenceNumberString,
  displayStatusId: dto?.displayStatus,
  lifecycleStatusId: dto?.status,
  eligibleAmount: dto?.eligibleAmount,
  duesAmount: dto?.duesAmount,
  currency: dto?.currency,
  createdAt: dto?.createdAt,
  processedAt: dto?.processedAt,
  createdAtLabel: formatDate ? formatDate(dto?.createdAt) : dto?.createdAt,
  processedAtLabel: getProcessedAtLabel(dto?.processedAt, formatDetailDate),
  releaseDateLabel: formatDate
    ? formatDate(dto?.processedAt || dto?.createdAt)
    : dto?.processedAt || dto?.createdAt,
});

export const mapTransfersPage = (
  response,
  { page = 1, pageSize = TRANSFERS_PAGE_SIZE, formatDate } = {},
) => {
  if (!Array.isArray(response)) {
    throw createTransfersRequestError();
  }

  return createTransferPage({
    page,
    pageSize,
    transfers: response.map(dto => mapTransferDto(dto, formatDate)),
  });
};

const mapNestedTransaction = (dto, transferCurrency, formatDate) => ({
  id: dto?.transactionKey,
  sourceName: dto?.fromAccountName,
  destinationName: dto?.toAccountName,
  productId: dto?.paymentProductId,
  directionId: dto?.transactionDirectionId,
  viewId: dto?.transactionViewId,
  statusId: dto?.transactionStatusId,
  amount: dto?.amount,
  currency: dto?.currency || transferCurrency,
  occurredAt: dto?.timestamp,
  occurredAtLabel: formatDate ? formatDate(dto?.timestamp) : dto?.timestamp,
});

export const mapTransferDetail = (
  response,
  formatDate,
  formatDetailDate = formatTransferDetailDate,
) => {
  if (response == null || typeof response !== 'object' || Array.isArray(response)) {
    throw createTransfersRequestError();
  }

  return createTransferDetail({
    ...mapTransferDto(response, formatDate, formatDetailDate),
    transactionCount: response.transactionCount,
    transactions: Array.isArray(response.transactions)
      ? response.transactions.map(dto =>
          mapNestedTransaction(dto, response.currency, formatDetailDate),
        )
      : [],
  });
};

export const mapTransferContent = localization => {
  const table = localization?.transfersTable || {};
  return {
    id: table.id,
    date: table.date,
    duesAmount: table.duesAmount,
    payment: table.payment,
    allStatus: table.allStatus,
    status: table.status,
    releaseDate: table.releaseDate,
    amount: table.amount,
    actions: table.actions,
    details: table.details,
    loading: valueOrFallback(table.loading, 'Loading transfers...'),
    error: valueOrFallback(table.error, 'Failed to load transfers. Please try again.'),
    retry: valueOrFallback(table.retry, 'Retry'),
    unknownStatus: valueOrFallback(table.unknown, 'Unknown'),
    statuses: {
      [TRANSFER_STATUS.PENDING]: table.pending,
      [TRANSFER_STATUS.COMPLETED]: table.completed,
      [TRANSFER_STATUS.FAILED]: table.failed,
      [TRANSFER_STATUS.CANCELED]: table.canceled,
      [TRANSFER_STATUS.UNKNOWN]: valueOrFallback(table.unknown, 'Unknown'),
    },
    empty: localization?.noPayments,
  };
};

export const mapTransferDetailContent = (localization, tableLocalization, transactionsTable) => {
  const table = tableLocalization || {};
  const nested = transactionsTable || {};
  return {
    transferDetails: localization?.transferDetails,
    confirmationNo: localization?.confirmationNo,
    status: localization?.status,
    date: localization?.date,
    totalAmount: localization?.totalAmount,
    duesAmount: localization?.duesAmount,
    transferAmount: localization?.transferAmount,
    transactions: localization?.transactions,
    transaction: localization?.transaction,
    loadMore: localization?.loadMore,
    supportNumber: `${localization?.supportNumber} : 16676`,
    supportHref: 'tel:16676',
    error: valueOrFallback(
      localization?.failedToLoadTransferDetails,
      'Failed to load transfer details. Please try again.',
    ),
    retry: valueOrFallback(localization?.retryButton, 'Retry'),
    unknownStatus: valueOrFallback(table.unknown, 'Unknown'),
    statuses: {
      [TRANSFER_STATUS.PENDING]: table.pending,
      [TRANSFER_STATUS.COMPLETED]: table.completed,
      [TRANSFER_STATUS.FAILED]: table.failed,
      [TRANSFER_STATUS.CANCELED]: table.canceled,
      [TRANSFER_STATUS.UNKNOWN]: valueOrFallback(table.unknown, 'Unknown'),
    },
    transactionStatuses: {
      [TRANSACTION_STATUS.PENDING]: nested.pending,
      [TRANSACTION_STATUS.COMPLETED]: nested.completed,
      [TRANSACTION_STATUS.FAILED]: nested.failed,
      [TRANSACTION_STATUS.ON_HOLD]: nested.onhold,
      [TRANSACTION_STATUS.UNKNOWN]: valueOrFallback(
        nested.unknown,
        valueOrFallback(table.unknown, 'Unknown'),
      ),
    },
  };
};

export const getTransfersLayout = width =>
  width > TRANSFERS_DESKTOP_MIN_WIDTH ? 'desktop' : 'mobile';
